import ModalContainer from '@/components/molecules/ModalContainer';
import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Center, Image, Text, VStack } from '@chakra-ui/react';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import iconsPng from '@/assets/img/png/icons';
import CustomButton from '@/components/atoms/CustomButton';
import { TRosterUserPeerSupportCertificateInfo } from '@/types/Roster.types';
import { useAuthContext } from '@/context/auth/useAuthContext';
import PeerSupportCertificationForm from '@/modules/organization/PeerSupportCertificationForm';

interface Props {
  peerSupportModalVisible: boolean;
  setPeerSupportModalVisible: Dispatch<SetStateAction<boolean>>;
  itemToUpgradeToPeerSupportName: string;
  onClickUpgrade: (peerSupportUpgradeInfo: TRosterUserPeerSupportCertificateInfo) => void;
  indexToUpgradePeerSupportData: number;
}

const addPeerSupportCertificationSchema = yup
  .object()
  .shape({
    dateOfIssue: yup.string().required('Date of Issue is required.'),
    validUntil: yup.string().required('Valid Until is required.'),
    certificateId: yup.string().required('Certificate ID is required.'),
  })
  .required();

const OrganizationOnboardingPeerSupportModal = ({
  peerSupportModalVisible,
  setPeerSupportModalVisible,
  itemToUpgradeToPeerSupportName,
  onClickUpgrade,
  indexToUpgradePeerSupportData,
}: Props) => {
  const { isSuperAdmin } = useAuthContext();

  const methods = useForm<TRosterUserPeerSupportCertificateInfo>({
    resolver: yupResolver(addPeerSupportCertificationSchema),
    mode: 'all',
    defaultValues: {
      dateOfIssue: '',
      validUntil: '',
      certificateId: '',
    },
  });

  const { getValues, reset: resetUserForm, formState } = methods;

  const { isValid } = formState;

  const onClickUpgradeLocal = useCallback(async () => {
    const peerSupportUpgradeInfo: TRosterUserPeerSupportCertificateInfo = getValues();
    onClickUpgrade(peerSupportUpgradeInfo);
    resetUserForm();
    setPeerSupportModalVisible(false);
  }, [getValues, indexToUpgradePeerSupportData]);

  return (
    <ModalContainer
      isOpen={peerSupportModalVisible}
      onClose={() => setPeerSupportModalVisible(false)}
      isBlur={false}
      width={'40%'}
      height={'20vh'}
      backgroundColor={'background.lightBlue'}
    >
      <Text variant={'loraTitle'} textAlign={'center'} fontSize={'26px'} marginTop={'20px'} whiteSpace={'pre-wrap'}>
        Upgrade Member to Peer Support
      </Text>
      <Center marginTop={'20px'}>
        <Image alt="add-member" src={iconsPng.handshake} width={120} height={120} />
      </Center>
      <VStack marginTop={'40px'}>
        <Text variant={'urbanistSemiBoldLarge'} color={'text.mediumBlue'} maxWidth={'300px'}>
          Add information about {itemToUpgradeToPeerSupportName}’s certification below.
        </Text>
      </VStack>
      <Center>
        <VStack marginY={'40px'} width={FORM_FIELD_WIDTH} gap={'20px'}>
          <PeerSupportCertificationForm methods={methods} />

          <CustomButton
            style={{ marginTop: '20px' }}
            backgroundColor="primary.500"
            label="Confirm"
            width={FORM_FIELD_WIDTH}
            labelColor="white"
            onClick={onClickUpgradeLocal}
            disabled={!isValid || isSuperAdmin}
          />
        </VStack>
      </Center>
    </ModalContainer>
  );
};

export default OrganizationOnboardingPeerSupportModal;
